import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useHybridAuthToken } from '../../components/hybridAuthToken';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Typography, FormControl, Select, MenuItem, TextField, FormControlLabel } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { DateTime } from 'luxon';
import axios from 'axios';
import useEnvConfig from '../../hooks/useEnvConfig';
import rollbar from '../../utils/rollbar';
import { BILLING_CONTACT_PATH, pathToRoute } from '../../constants/routes';
import DialogContentText from '@material-ui/core/DialogContentText';
import { useHistory } from 'react-router-dom';
import { validateEmail } from '../../utils/validations';

const addOrdinal = (number) => {
  const leadingZeroRemoved = parseInt(number, 10).toString();
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = leadingZeroRemoved % 100;
  return leadingZeroRemoved + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

// Helper function to find due date object by value
const findDueDateByValue = (value, dueDatesArray) => {
  return dueDatesArray.find((dueDate) => dueDate.value === value);
};

const ChangeDueDate = ({ userInfo, setCurrentLastDueDateUpdate }) => {
  const hybridAuth = useHybridAuthToken();
  const history = useHistory();
  const { paymentServiceAPIUrl } = useEnvConfig();
  const { prospectId, contactId, lastDueDate, listOfDueDates, email, firstName, lastName, contractId } = userInfo;

  const currentDueDate = lastDueDate ? DateTime.fromISO(lastDueDate, { zone: 'utc' }).day.toString() : '';
  const listOfDueDatesArray = listOfDueDates ? listOfDueDates.split(',') : [];
  const listOfDueDatesArrayWithOrdinal = listOfDueDatesArray.map((dueDate) => ({
    text: addOrdinal(dueDate),
    value: dueDate,
  }));

  const currentDueDateObject = findDueDateByValue(currentDueDate, listOfDueDatesArrayWithOrdinal);
  const currentDueDateText = currentDueDateObject ? currentDueDateObject.text : '';

  const [selectedDueDate, setSelectedDueDate] = useState(currentDueDate);
  const [selectedDueDateText, setSelectedDueDateText] = useState(currentDueDateText);
  const [confirmationEmail, setConfirmationEmail] = useState();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isFormValid, setIsFormValid] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const emailProvided = confirmationEmail || email;
  const isCurrentDueDateSelected = selectedDueDate === currentDueDate;
  const oneYearFromNow = DateTime.now().plus({ years: 1 }).toFormat('MM/dd/yyyy');
  const acknowledgmentText = [
    `You, ${firstName} ${lastName}, acknowledge that you are changing your invoice due date`,
    `from ${currentDueDateText} each month to the ${selectedDueDateText} of each month. If you are enrolled in`,
    `Autopay, payments will occur on the first business day before the ${selectedDueDateText}. You further acknowledge`,
    `that this date cannot be updated again for one year from today, ${oneYearFromNow}, that you may be charged twice`,
    `in your first month, and that this change will be reflected on your NEXT BILLING PERIOD.`,
  ].join(' ');

  useEffect(() => {
    setSelectedDueDate(currentDueDate);
  }, [currentDueDate]);

  useEffect(() => {
    setIsFormValid(acceptTerms && emailProvided && isEmailValid && !isCurrentDueDateSelected);
  }, [acceptTerms, emailProvided, isEmailValid, isCurrentDueDateSelected]);

  const handleSelectedDueDateChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedDueDate(selectedValue);

    // Find the corresponding object in listOfDueDatesArrayWithOrdinal
    const selectedDueDateObject = findDueDateByValue(selectedValue, listOfDueDatesArrayWithOrdinal);
    const selectedText = selectedDueDateObject ? selectedDueDateObject.text : '';

    setSelectedDueDateText(selectedText);
  };

  const handleEmailChange = (e) => {
    setConfirmationEmail(e.target.value);

    const valid = validateEmail(e.target.value);
    setIsEmailValid(valid);
  };

  const handleAcceptTerms = () => {
    setAcceptTerms(!acceptTerms);
  };

  const submitChangeDueDate = () => {
    handleChangeDueDate();
  };

  const handleChangeDueDate = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${paymentServiceAPIUrl}/billing-due-date`,
        {
          serviceContractId: contractId,
          newDueDate: selectedDueDate,
          email: emailProvided,
          prospectId,
        },
        { headers: { Authorization: hybridAuth } },
      );

      if (response.status === 200) {
        setCurrentLastDueDateUpdate(DateTime.now().toISO());
        history.replace(pathToRoute(BILLING_CONTACT_PATH, { prospectId, contactId }));
      } else {
        rollbar.warn(`Change due date encountered an unhandled status: ${response.status} prospect: ${prospectId}`);
      }
    } catch (error) {
      setHasError(true);
      rollbar.error(`Change due date failure prospectId: ${prospectId} error: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!hybridAuth) return <LinearProgress />;

  return (
    <div
      role="region"
      aria-live="assertive"
      data-testid="change-due-date-container"
      style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
    >
      <Typography variant="h5" component="span">
        Change Due Date
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <Typography variant="body2" component="span">
          Change will not take effect until the next bill.
        </Typography>
        <FormControl style={{ width: '244px' }}>
          <Typography variant="body2" component="span" style={{ color: '#B3B3B3' }}>
            Monthly due date
          </Typography>
          <Select value={selectedDueDate} defaultValue={currentDueDate} onChange={handleSelectedDueDateChange}>
            <MenuItem value="">Select a due date</MenuItem>
            {listOfDueDatesArrayWithOrdinal.map((dueDate) => (
              <MenuItem key={dueDate.text} value={dueDate.value}>
                {dueDate.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ width: '660px' }}>
          <Typography variant="body2" component="span">
            Confirmation email
          </Typography>
          <TextField
            hiddenLabel
            id="email-input"
            variant="outlined"
            inputProps={{ 'data-testid': 'email-input' }}
            placeholder={email}
            value={confirmationEmail}
            error={!isEmailValid}
            helperText={!isEmailValid ? 'Please enter a valid email address' : ''}
            onChange={handleEmailChange}
            style={{ borderRadius: '8px' }}
          />
        </FormControl>

        <FormControlLabel
          control={<Checkbox name="acceptTerms" color="primary" onChange={handleAcceptTerms} />}
          label="Read to customer, then check the box if they agree:"
          data-testid="acceptTerms"
        />
        <Typography variant="body2" component="span" style={{ color: '#616578' }}>
          {acknowledgmentText}
        </Typography>

        <Button
          variant="contained"
          onClick={submitChangeDueDate}
          disabled={!isFormValid}
          data-testid="change-due-date-submit-btn"
          style={{
            width: '194px',
            borderRadius: '30px',
            color: '#FFFFFF',
            backgroundColor: !isFormValid ? '#B6B8C0' : '#1F2647',
          }}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Change Due Date'}
        </Button>
        {hasError && <DialogContentText color="error">Something went wrong. Try again later.</DialogContentText>}
      </div>
    </div>
  );
};

ChangeDueDate.propTypes = {
  userInfo: PropTypes.shape({
    prospectId: PropTypes.string.isRequired,
    contactId: PropTypes.string.isRequired,
    contractId: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    mobilePhoneNumber: PropTypes.string,
    phoneNumber: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    lastDueDate: PropTypes.string,
    listOfDueDates: PropTypes.string,
    lastDueDateUpdate: PropTypes.string,
    purchasedThru: PropTypes.string,
    totalAmountDue: PropTypes.string,
  }),
  specialInvoiceAmount: PropTypes.number,
  referenceNumberPrefix: PropTypes.string,
  isBillingEscalations: PropTypes.bool,
  setCurrentLastDueDateUpdate: PropTypes.func,
};

export default ChangeDueDate;
